import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { getToken } from 'firebase/app-check';
import { Role } from '../constants/roles.constants';
import type { DriverLoginChannel } from '../types/accounts.types';
import {
  signOut,
  onAuthStateChange,
  firebaseAuth,
  appCheckInstance,
} from '../utils/firebase';
import { login } from '../api/user.api';
import { useToast } from '../contexts/toast-context';
import { TOAST_TYPES } from '../constants/common.constants';
import {
  initializeFCMForUser,
  cleanupFCMTokenForDevice,
  PUSH_ROLES,
} from '../lib/fcm/init';
import userIsAuthorized from '../utils/userIsAuthorized';
import {
  getCachedProfile,
  setCachedProfile,
  clearCachedProfile,
} from '../utils/profile-cache';
import { rollbar } from '../lib/rollbar';
import { mixpanelService } from '../lib/mixpanel';

export interface UserProfile {
  uuid: string;
  name: string;
  /** Null for drivers on the Driver ID channel, who have no email address. */
  email: string | null;
  role: Role;
  account: string | null;
  hierarchyLevel: {
    name: string;
    uuid: string;
  } | null;
  /** True while a driver is still on a temporary password and owes us a real one. */
  mustSetPassword?: boolean;
  /** The user's own organisation's driver login channel. */
  driverLoginChannel?: DriverLoginChannel;
}

export interface AuthContextType {
  user: UserProfile | null;
  setUser: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  isAuthenticated: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  updateUserProfile: (updates: Partial<UserProfile>) => void;
  setSkipFetch: (skip: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { addToast } = useToast();
  const [user, setUser] = useState<AuthContextType['user']>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Flag to skip fetch on auth state change (used by login/activation pages)
  const skipFetchRef = useRef(false);

  const setSkipFetch = (skip: boolean) => {
    skipFetchRef.current = skip;
  };

  const updateUserProfile = (updates: Partial<UserProfile>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : null));
  };

  const fetchUserProfile = async (firebaseUser: FirebaseUser) => {
    try {
      const response = await login(); // Calls /user/login endpoint
      const userData: UserProfile = {
        ...response.user,
        role: response.user.role as Role, // ensure correct type
      };
      setUser(userData);
      setIsAuthenticated(true);
      setCachedProfile(userData);
      // Configure Rollbar with user info so errors can be associated with the person in Rollbar dashboard
      rollbar.configure({
        payload: {
          person: { id: userData.uuid, email: userData.email ?? undefined },
        },
      });
      // Drivers on the Driver ID channel have no email; fall back to their uuid so the
      // analytics identity stays stable rather than empty.
      mixpanelService.identify(
        userData.uuid,
        userData.name,
        userData.email ?? userData.uuid,
      );

      // Register or cleanup FCM token based on role
      // Drivers, managers, executives -> register the device for push notifications, previously registered tokens for this device will be reassigned to the new user
      // Others -> cleanup any existing token for this device as it changes ownership
      if (userIsAuthorized(userData.role, PUSH_ROLES)) {
        initializeFCMForUser();
      } else {
        cleanupFCMTokenForDevice();
      }
    } catch (error) {
      // On network error, keep session alive using cached profile if available
      const isNetworkError =
        error instanceof TypeError &&
        (error.message === 'Load failed' ||
          error.message === 'Failed to fetch' ||
          error.message.toLowerCase().includes('network'));

      if (isNetworkError && firebaseUser) {
        const cached = getCachedProfile<UserProfile>();
        if (cached) setUser(cached);
        setIsAuthenticated(true);
        addToast(
          'Network error occurred. Please check your internet connection and try again.',
          {
            type: TOAST_TYPES.ERROR,
          },
        );
        return;
      }

      addToast(`${error}`, { type: TOAST_TYPES.ERROR });
      await signOut();
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChange(
      async (firebaseUser: FirebaseUser | null) => {
        if (firebaseUser && !skipFetchRef.current) {
          // Fetch user profile on page refresh (when Firebase session persists)
          await fetchUserProfile(firebaseUser);
        } else if (!firebaseUser) {
          // Clear when Firebase user logs out
          setUser(null);
          setIsAuthenticated(false);
          clearCachedProfile();
          // Clear Rollbar user context on logout
          rollbar.configure({ payload: { person: undefined } });
          mixpanelService.reset();
        }
        // Reset skip flag after handling
        skipFetchRef.current = false;
        setIsLoading(false);
      },
    );

    // Pre-warm Firebase ID + App Check tokens when the tab becomes visible.
    // Mobile browsers throttle background timers, so isTokenAutoRefreshEnabled
    // doesn't fire mid-idle; without this, the first post-idle click pays the
    // full token-refresh round trip on a cold mobile radio.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const u = firebaseAuth.currentUser;
      if (!u) return;
      u.getIdToken(true).catch(() => null);
      if (appCheckInstance) {
        getToken(appCheckInstance, false).catch(() => null);
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isAuthenticated,
        setIsAuthenticated,
        isLoading,
        updateUserProfile,
        setSkipFetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Auth context if there is one, `undefined` otherwise — for components that
 * can do something sensible without a provider and shouldn't force every
 * caller (or test) to supply one. Prefer `useAuth` when the component
 * genuinely cannot work unauthenticated.
 */
export function useOptionalAuth() {
  return useContext(AuthContext);
}
